import React, { useState, useEffect } from 'react';

interface ClassicOrganizationInfoProps {
    onMenuSelect: (menuId: string) => void;
    tenantName: string;
    currentUser: any;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

interface OrganizationInfo {
    tenant_code: string;
    tenant_name: string;
    postal_code: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
    representative_name: string | null;
    tenant_type?: string | null;
    houjin_bangou?: string | null;
    service_kubun_code?: string | null;
    wam_kyoten_name?: string | null;
    wam_jigyosho_name?: string | null;
}

const ClassicOrganizationInfo: React.FC<any> = ({
    apiFetch,
    currentTenantId,
}) => {
    const [orgInfo, setOrgInfo] = useState<OrganizationInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Partial<OrganizationInfo>>({});

    // データ取得
    const fetchOrgInfo = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiFetch('/api/organization');
            if (!response.ok) {
                throw new Error('組織情報の取得に失敗しました');
            }
            const data = await response.json();
            setOrgInfo(data);
            setFormData(data);
        } catch (err: any) {
            setError(err.message || '組織情報の取得中にエラーが発生しました');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentTenantId) {
            fetchOrgInfo();
        }
    }, [currentTenantId]);

    // 更新
    const handleUpdate = async () => {
        try {
            setError(null);
            const response = await apiFetch('/api/organization', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tenant_name: formData.tenant_name,
                    postal_code: formData.postal_code || null,
                    address: formData.address || null,
                    phone: formData.phone || null,
                    email: formData.email || null,
                    representative_name: formData.representative_name || null,
                    houjin_bangou: formData.houjin_bangou ?? null,
                    service_kubun_code: formData.service_kubun_code ?? null,
                    wam_kyoten_name: formData.wam_kyoten_name ?? null,
                    wam_jigyosho_name: formData.wam_jigyosho_name ?? null,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '組織情報の更新に失敗しました');
            }

            await fetchOrgInfo();
            setIsEditing(false);
        } catch (err: any) {
            setError(err.message || '組織情報の更新中にエラーが発生しました');
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', fontFamily: "'MS UI Gothic', sans-serif" }}>
                読み込み中...
            </div>
        );
    }

    if (!orgInfo) {
        return (
            <div style={{ padding: '20px', fontFamily: "'MS UI Gothic', sans-serif" }}>
                <div style={{ color: '#c33', marginBottom: '16px' }}>
                    組織情報が見つかりません
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '16px', fontFamily: "'MS UI Gothic', sans-serif", fontSize: '11px' }}>
            <div style={{ marginBottom: '16px' }}>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '13px' }}>81: 法人・施設情報</h2>
                <p style={{ margin: '0 0 16px 0', color: '#666' }}>
                    法人および施設の基本情報を管理します
                </p>
            </div>

            {error && (
                <div style={{
                    backgroundColor: '#fee',
                    color: '#c33',
                    padding: '8px 12px',
                    marginBottom: '16px',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                }}>
                    {error}
                </div>
            )}

            {!isEditing && (
                <div style={{ marginBottom: '16px' }}>
                    <button
                        onClick={() => setIsEditing(true)}
                        className="classic-button"
                        style={{ padding: '6px 16px' }}
                    >
                        編集
                    </button>
                </div>
            )}

            <fieldset className="classic-fieldset">
                <legend className="classic-legend">施設情報</legend>
                <div style={{ display: 'grid', gap: '16px', maxWidth: '600px' }}>
                    {/* テナントコード（読み取り専用） */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                            テナントコード
                        </label>
                        <div style={{ padding: '6px', backgroundColor: '#f5f5f5', border: '1px solid #ccc' }}>
                            {orgInfo.tenant_code}
                        </div>
                    </div>

                    {/* 施設名 */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                            施設名 <span style={{ color: 'red' }}>*</span>
                        </label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={formData.tenant_name || ''}
                                onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                                style={{ width: '100%', padding: '6px', fontSize: '11px' }}
                            />
                        ) : (
                            <div style={{ padding: '6px' }}>{orgInfo.tenant_name}</div>
                        )}
                    </div>

                    {/* 郵便番号 */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                            郵便番号
                        </label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={formData.postal_code || ''}
                                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                style={{ width: '200px', padding: '6px', fontSize: '11px' }}
                                placeholder="例: 123-4567"
                            />
                        ) : (
                            <div style={{ padding: '6px' }}>{orgInfo.postal_code || '－'}</div>
                        )}
                    </div>

                    {/* 住所 */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                            住所
                        </label>
                        {isEditing ? (
                            <textarea
                                value={formData.address || ''}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                style={{ width: '100%', padding: '6px', fontSize: '11px', minHeight: '60px' }}
                                placeholder="例: 東京都〇〇区..."
                            />
                        ) : (
                            <div style={{ padding: '6px', whiteSpace: 'pre-wrap' }}>{orgInfo.address || '－'}</div>
                        )}
                    </div>

                    {/* 電話番号 */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                            電話番号
                        </label>
                        {isEditing ? (
                            <input
                                type="tel"
                                value={formData.phone || ''}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                style={{ width: '200px', padding: '6px', fontSize: '11px' }}
                                placeholder="例: 03-1234-5678"
                            />
                        ) : (
                            <div style={{ padding: '6px' }}>{orgInfo.phone || '－'}</div>
                        )}
                    </div>

                    {/* メールアドレス */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                            メールアドレス
                        </label>
                        {isEditing ? (
                            <input
                                type="email"
                                value={formData.email || ''}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                style={{ width: '100%', padding: '6px', fontSize: '11px' }}
                                placeholder="例: info@example.com"
                            />
                        ) : (
                            <div style={{ padding: '6px' }}>{orgInfo.email || '－'}</div>
                        )}
                    </div>

                    {/* 代表者名 */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                            代表者名
                        </label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={formData.representative_name || ''}
                                onChange={(e) => setFormData({ ...formData, representative_name: e.target.value })}
                                style={{ width: '100%', padding: '6px', fontSize: '11px' }}
                                placeholder="例: 山田 太郎"
                            />
                        ) : (
                            <div style={{ padding: '6px' }}>{orgInfo.representative_name || '－'}</div>
                        )}
                    </div>
                </div>
            </fieldset>

            <fieldset className="classic-fieldset" style={{ marginTop: '16px' }}>
                <legend className="classic-legend">WAM NET 設定</legend>
                <div style={{ display: 'grid', gap: '16px', maxWidth: '600px' }}>
                    {orgInfo.tenant_type === 'CORPORATION' && (
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>法人番号（13桁）</label>
                            {isEditing ? (
                                <input type="text" maxLength={13} value={formData.houjin_bangou || ''}
                                    onChange={(e) => setFormData({ ...formData, houjin_bangou: e.target.value })}
                                    style={{ width: '100%', padding: '6px', fontSize: '11px' }} />
                            ) : (
                                <div style={{ padding: '6px' }}>{orgInfo.houjin_bangou || '－'}</div>
                            )}
                        </div>
                    )}
                    {orgInfo.tenant_type === 'FACILITY' && (
                        <>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>サービス区分コード（8桁）</label>
                                {isEditing ? (
                                    <input type="text" maxLength={8} value={formData.service_kubun_code || ''}
                                        onChange={(e) => setFormData({ ...formData, service_kubun_code: e.target.value })}
                                        style={{ width: '100%', padding: '6px', fontSize: '11px' }} />
                                ) : (
                                    <div style={{ padding: '6px' }}>{orgInfo.service_kubun_code || '－'}</div>
                                )}
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>WAM NET 拠点名称</label>
                                {isEditing ? (
                                    <input type="text" value={formData.wam_kyoten_name || ''}
                                        onChange={(e) => setFormData({ ...formData, wam_kyoten_name: e.target.value })}
                                        style={{ width: '100%', padding: '6px', fontSize: '11px' }} />
                                ) : (
                                    <div style={{ padding: '6px' }}>{orgInfo.wam_kyoten_name || '－'}</div>
                                )}
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>WAM NET 事業所名</label>
                                {isEditing ? (
                                    <input type="text" value={formData.wam_jigyosho_name || ''}
                                        onChange={(e) => setFormData({ ...formData, wam_jigyosho_name: e.target.value })}
                                        style={{ width: '100%', padding: '6px', fontSize: '11px' }} />
                                ) : (
                                    <div style={{ padding: '6px' }}>{orgInfo.wam_jigyosho_name || '－'}</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </fieldset>

            {isEditing && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', maxWidth: '600px' }}>
                    <button onClick={handleUpdate} className="classic-button" disabled={!formData.tenant_name}>保存</button>
                    <button onClick={() => { setIsEditing(false); setFormData(orgInfo); }}
                        className="classic-button" style={{ backgroundColor: '#ddd' }}>キャンセル</button>
                </div>
            )}
        </div>
    );
};

export default ClassicOrganizationInfo;
